import React, { useEffect, useRef, useState } from "react";
import MenuBuilder from "react-dnd-menu-builder";
import {
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useAppServices } from "hook/services";
import FormField from "layouts/pages/account/components/FormField";
import { useAgencyInfo } from "context/agency";
import toast from "react-hot-toast";
import PrimaryButton from "../../../../../components/PrimaryButton";
import env from "config";

const staticMenus = ["home", "blogs", "news", "events", "jobs", "pricing", "deals"];
const menuUrlMapping = {
  home: "/",
  blogs: "/blogs",
  news: "/news-feeds",
  events: "/events",
  jobs: "/jobs",
  pricing: "/plans",
  deals: "/deals",
};

// The drag & drop tree uses `id` as its React key and as the lookup key for move
// and delete. Two items sharing an id make delete remove the wrong row and let a
// drag duplicate branches, so every item must carry a unique one.
let menuIdCounter = 0;
const createMenuId = (name) =>
  `${name || "item"}-${Date.now()}-${(menuIdCounter += 1)}-${Math.random()
    .toString(36)
    .substring(2, 9)}`;

const normalizeHref = (href) =>
  (href ?? "")
    .toString()
    .trim()
    .replace(/^\/+|\/+$/g, "")
    .toLowerCase();

const normalizeName = (name) => (name ?? "").toString().trim().toLowerCase();

// Identity of a menu entry regardless of its id, used to detect duplicates.
// Listing labels are matched by name because re-seeding an agency can create the
// same label twice under different slugs (`bakeries`, `bakeries-1`).
const getMenuIdentity = (item) => {
  const type = item?.type || "static";
  const name = normalizeName(item?.name);
  const href = normalizeHref(item?.href);

  if (type === "tag") return `tag|${name || href}`;
  if (type === "customLink") return `customLink|${href}|${name}`;
  return `${type}|${href || name}`;
};

// Duplicates only matter between siblings: the same listing label may legitimately
// sit under two different parents. "Add to Menu" always appends at the top level,
// so adding is guarded against the top level rather than the whole tree.
const levelHasIdentity = (items, identity) =>
  (items || []).some((item) => getMenuIdentity(item) === identity);

const Menu = ({ handelThemeData, selectedTheme }) => {
  const [menuState, setMenuState] = useState([]);
  const [footerMenuState, setFooterMenuState] = useState([]);
  const loadedMenuSignature = useRef(null);
  const loadedFooterSignature = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const [openWhitelabelModal, setOpenWhitelabelModal] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [agency, Update] = useAgencyInfo();
  const [businessTags, setBusinessTags] = useState([]);
  const [menuType, setMenuType] = useState("Header Menu");
  const [businesstagLoader, setbusinesstagLoader] = useState(false);

  const [enabledHomeToggle, setEnabledHomeToggle] = useState(true);

  const handleMenuTypeChange = (e) => {
    const selectedType = e.target.value;
    setMenuType(selectedType);
  };

  const [businessMenu, setBusinessMenu] = useState({
    business_tags: [],
  });
  const [openSections, setOpenSections] = useState({
    static: false,
    pages: false,
    tags: false,
    customLinks: false,
  });
  const [tagSearchInput, setTagSearchInput] = useState("");
  const [filteredTags, setFilteredTags] = useState([]);
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [isCreatingTag, setIsCreatingTag] = useState(false);
  const [errorLink, setError] = useState("");
  const [funnels, setFunnels] = useState([]);
  const [selectedFunnel, setSelectedFunnel] = useState(null);
  const [selectedStep, setSelectedStep] = useState(null);
  const [pagesForSelectedStep, setPagesForSelectedStep] = useState([]);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [pendingChecked, setPendingChecked] = useState(null);
  const [funnelData, setfunneldata] = useState({
    name: "",
    url: "",
  });
  const [CustomValue, setCustomValue] = useState({
    name: "",
    url: "",
  });
  const Service = useAppServices();

  const handleSelectChange = (selectedOptions, fieldName) => {
    setBusinessMenu((prev) => ({
      ...prev,
      [fieldName]: selectedOptions, // array of { label, value }
    }));
  };

  const handleTagSearch = (inputValue) => {
    setTagSearchInput(inputValue);

    if (inputValue.trim()) {
      const query = inputValue.toLowerCase();
      const filtered = businessTags.filter((tag) =>
        (tag.label || "").toLowerCase().includes(query)
      );

      setFilteredTags(filtered);
    } else {
      setFilteredTags(businessTags);
    }

    setShowTagDropdown(true);
  };

  const handleSelectTag = (tag) => {
    const isAlreadySelected = businessMenu.business_tags.some((t) => t.value === tag.value);
    if (!isAlreadySelected) {
      setBusinessMenu((prev) => ({
        ...prev,
        business_tags: [...prev.business_tags, tag],
      }));
    }
    setTagSearchInput("");
    setShowTagDropdown(false);
  };

  const handleRemoveTag = (tagValue) => {
    setBusinessMenu((prev) => ({
      ...prev,
      business_tags: prev.business_tags.filter((t) => t.value !== tagValue),
    }));
  };

  const handleCreateNewTag = async () => {
    if (!tagSearchInput.trim()) {
      toast.error("Please enter a tag name");
      return;
    }

    setIsCreatingTag(true);
    try {
      // Immediately add tag to the array with optimistic update
      const tempTag = {
        label: tagSearchInput,
        value: tagSearchInput.toLowerCase().replace(/\s+/g, "-"),
      };

      const isAlreadySelected = businessMenu.business_tags.some((t) => t.value === tempTag.value);

      // Create the tag on the server
      await createTags(tagSearchInput);
      // Refresh tags from server to get actual values
      const tags = await fetchTags();
      if (tags) {
        const createdTag = tags.find((t) => t.label === tagSearchInput);
        console.log("Created Tag:", createdTag);
        if (createdTag && !isAlreadySelected) {
          setBusinessMenu((prev) => ({
            ...prev,
            business_tags: [...prev.business_tags, createdTag],
          }));
        }
      }

      toast.success(`Tag "${tagSearchInput}" created successfully`);
      setTagSearchInput("");
      setShowTagDropdown(false);
    } catch (error) {
      console.error("Error creating tag:", error);
      toast.error("Failed to create tag");
      // Remove the tag from array if creation failed
      setBusinessMenu((prev) => ({
        ...prev,
        business_tags: prev.business_tags.filter((t) => t.label !== tagSearchInput),
      }));
    } finally {
      setIsCreatingTag(false);
    }
  };

  const agencyId = selectedTheme?.agency_id || agency?._id;

  const handleGetFunnel = async () => {
    if (!agencyId) return;
    try {
      const queryParams = new URLSearchParams({ agency_id: agencyId });
      const { response } = await Service.webhook.GetFunnels({ query: queryParams.toString() });
      setFunnels(response.data.data.funnels);
    } catch (error) {
      console.error("Error fetching funnels:", error);
    }
  };

  const handleFunnelChange = (e) => {
    const funnelId = e.target.value;
    const funnel = funnels.find((f) => f._id === funnelId);
    setSelectedFunnel(funnel);
    setSelectedStep(null);
    setPagesForSelectedStep([]);
    setfunneldata((prev) => ({ ...prev, url: `${env.GHL.PREVIEW_BASE_URL || "https://app.gohighlevel.com/v2/preview"}/` }));
  };

  const handleStepChange = (e) => {
    const stepId = e.target.value;
    const selectedStepData = selectedFunnel.steps.find((s) => s.id === stepId);
    setSelectedStep(selectedStepData);
    setPagesForSelectedStep(selectedStepData.pages);
    setfunneldata((prev) => ({ ...prev, url: "" }));
  };

  const fetchTags = async () => {
    if (!agencyId) return;
    const { response } = await Service.business_tags.get({
      query: `agency_id=${agencyId}`,
    });

    if (response?.success && Array.isArray(response.data)) {
      const sorted = [...response.data].sort((a, b) => a.order - b.order);
      const data = sorted.map((tag) => ({
        label: tag.name,
        value: tag.slug,
      }));

      setBusinessTags(data);
      setFilteredTags(data);
      return data;
    }
  };

  const createTags = async (tagName) => {
    setbusinesstagLoader(true);
    try {
      const payload = {
        name: tagName,
        agency_id: agencyId,
      };

      const { response } = await Service.business_tags.create({ payload });
      return response;
    } finally {
      setbusinesstagLoader(false);
    }
  };

  // Menu mounts with the other branding tabs, before the theme (and its
  // agency id) has loaded. A one-shot fetch on mount always queried
  // agency_id=undefined, so listing labels never appeared in the picker.
  useEffect(() => {
    if (!agencyId) return;
    fetchTags();
    handleGetFunnel();
  }, [agencyId]);

  // ✅ Load saved structure for header menu
  useEffect(() => {
    const inputData = selectedTheme?.theme_data?.menu_customize || {};

    // `selectedTheme` gets a new reference on every save, so reload only when the
    // stored menu actually changed — otherwise unsaved edits get wiped.
    const signature = JSON.stringify(inputData);
    if (signature === loadedMenuSignature.current) return;
    loadedMenuSignature.current = signature;

    const usedIds = new Set();
    const takeId = (savedId, name) => {
      const id = savedId && !usedIds.has(savedId) ? savedId : createMenuId(name);
      usedIds.add(id);
      return id;
    };

    const parseChildren = (items) => {
      if (!items) return [];
      const seen = new Set();
      return items.reduce((acc, item) => {
        const identity = getMenuIdentity(item);
        if (seen.has(identity)) return acc;
        seen.add(identity);

        acc.push({
          id: takeId(item.id, item.name),
          name: item.name,
          type: item.type,
          href: item.href,
          target: item.target,
          defaultName: item.defaultName,
          metaTitle: item.metaTitle || "",
          metaDescription: item.metaDescription || "",
          isEnable: item.visibility ?? true,
          children: parseChildren(item.subMenu),
        });
        return acc;
      }, []);
    };

    const seenRoots = new Set();
    const initialMenu = Object.entries(inputData).reduce((acc, [name, item]) => {
      const cleanName = name.replace(/-(page|customlink|tag)$/, "");
      const identity = getMenuIdentity({ ...item, name: cleanName });
      if (seenRoots.has(identity)) return acc;
      seenRoots.add(identity);

      acc.push({
        id: takeId(item.id, cleanName),
        name: cleanName,
        type: item.type,
        href: item.href,
        target: item.target,
        defaultName: item.defaultName,
        metaTitle: item.metaTitle || "",
        metaDescription: item.metaDescription || "",
        isEnable: item.visibility ?? true,
        children: parseChildren(item.subMenu),
      });
      return acc;
    }, []);

    setMenuState(initialMenu);
  }, [selectedTheme]);

  // ✅ Load saved structure for footer menu
  useEffect(() => {
    const inputData = selectedTheme?.theme_data?.footer_customize || {};

    const signature = JSON.stringify(inputData);
    if (signature === loadedFooterSignature.current) return;
    loadedFooterSignature.current = signature;

    const parseFooterItems = (items) => {
      if (!items) return [];
      return items.map((item) => ({
        id: item.name,
        name: item.name,
        type: item.type,
        href: item.href,
        target: item.target,
        metaTitle: item.metaTitle || "",
        metaDescription: item.metaDescription || "",
        isEnable: item.visibility ?? true,
        children: [], // Footer menu doesn't support nested items
      }));
    };

    const initialFooterMenu = Object.entries(inputData).map(([name, item]) => {
      const cleanName = name.replace(/-(page|customlink|tag)$/, "");

      return {
        id: name,
        name: cleanName,
        type: item.type,
        href: item.href,
        target: item.target,
        metaTitle: item.metaTitle || "",
        metaDescription: item.metaDescription || "",
        isEnable: item.visibility ?? true,
        children: [], // Footer menu doesn't support nested items
      };
    });

    setFooterMenuState(initialFooterMenu);
  }, [selectedTheme]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCustomValue((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const footerMenuExists = (menus, id) => {
    for (const m of menus) {
      if (m.id === id) return true;
    }
    return false;
  };

  // ✅ Add menu item to header builder
  const handleAddMenu = (menu) => {
    if (menuType === "Header Menu") {
      if (levelHasIdentity(menuState, getMenuIdentity(menu))) {
        toast.error(`"${menu.name}" is already in this menu`);
        return;
      }
      setMenuState((prev) => [
        ...prev,
        {
          ...menu,
          id: createMenuId(menu.name),
        },
      ]);
    } else {
      if (footerMenuExists(footerMenuState, menu.id)) return;
      setFooterMenuState([...footerMenuState, menu]);
    }
  };

  const handleAddCustomMenuLink = () => {
    if (!CustomValue?.name || !CustomValue?.url) return;

    if (menuType === "Header Menu") {
      const menu = {
        id: createMenuId(CustomValue.name),
        name: CustomValue.name,
        type: "customLink",
        href: CustomValue.url,
        target: false,
        isEnable: true,
        children: [],
      };
      if (levelHasIdentity(menuState, getMenuIdentity(menu))) {
        setError("Custom Link of this name already exist");
        return;
      }
      setError("");
      setMenuState([...menuState, menu]);
    } else {
      if (
        footerMenuExists(footerMenuState, CustomValue.name) ||
        footerMenuExists(footerMenuState, `${CustomValue.name}-customlink`)
      ) {
        setError("Custom Link of this name already exist");
        return;
      }
      const menu = {
        id: CustomValue.name,
        name: CustomValue.name,
        type: "customLink",
        href: CustomValue.url,
        target: false,
        isEnable: true,
        children: [],
      };
      setFooterMenuState([...footerMenuState, menu]);
    }

    setCustomValue({
      name: "",
      url: "",
    });
  };

  const handleAddFunnel = () => {
    if (!funnelData?.name || funnelData?.name === "") return;
    if (!funnelData?.url || funnelData?.url === "") return;

    if (menuType === "Header Menu") {
      const menu = {
        id: createMenuId(funnelData.name),
        name: funnelData.name,
        type: "pages",
        href: funnelData.url,
        target: false,
        isEnable: true,
        children: [],
      };
      if (levelHasIdentity(menuState, getMenuIdentity(menu))) {
        toast.error("Menu of this name already exist please use different name");
        return;
      }
      setMenuState([...menuState, menu]);
    } else {
      if (
        footerMenuExists(footerMenuState, funnelData?.name) ||
        footerMenuExists(footerMenuState, `${funnelData?.name}-page`)
      ) {
        toast.error("Menu of this name already exist please use different name");
        return;
      }
      const menu = {
        id: funnelData.name,
        name: funnelData.name,
        type: "pages",
        href: funnelData.url,
        target: false,
        isEnable: true,
        children: [],
      };
      setFooterMenuState([...footerMenuState, menu]);
    }

    setfunneldata({
      name: "",
      url: "",
    });
    setSelectedFunnel(null);
    setSelectedStep(null);
    setPagesForSelectedStep([]);
    setOpenWhitelabelModal(false);
  };

  const currentMenuItems = menuType === "Header Menu" ? menuState : footerMenuState;

  const tagToMenuItem = (tag) => ({
    name: tag.label,
    defaultName: tag?.label,
    type: "tag",
    href: tag.value,
    target: false,
    isEnable: true,
    children: [],
  });

  // Labels already at the top level are hidden; one still nested under a parent
  // stays offered so it can also be placed elsewhere.
  const availableTagOptions = filteredTags.filter(
    (tag) =>
      !businessMenu.business_tags.some((selected) => selected.value === tag.value) &&
      !levelHasIdentity(currentMenuItems, getMenuIdentity(tagToMenuItem(tag)))
  );

  const handleAddBusinessTags = () => {
    let tagsToAdd = businessMenu?.business_tags || [];

    // Typing the full label and clicking Add to Menu should work even when
    // the suggestion click was lost (the input blurs and unmounts the list).
    if (!tagsToAdd.length && tagSearchInput.trim()) {
      const typed = tagSearchInput.trim().toLowerCase();
      const match = businessTags.find((tag) => (tag.label || "").toLowerCase() === typed);
      if (match) tagsToAdd = [match];
    }

    if (!tagsToAdd.length) {
      toast.error("Select a listing label, or type its full name");
      return;
    }

    const staged = new Set();
    const skipped = [];
    const newMenus = [];

    tagsToAdd.forEach((tag) => {
      const item = tagToMenuItem(tag);
      const identity = getMenuIdentity(item);

      if (staged.has(identity) || levelHasIdentity(currentMenuItems, identity)) {
        skipped.push(tag.label);
        return;
      }

      staged.add(identity);
      newMenus.push({ ...item, id: createMenuId(tag?.label) });
    });

    if (skipped.length) {
      toast.error(`Already in this menu: ${skipped.join(", ")}`);
    }

    if (newMenus.length) {
      if (menuType === "Header Menu") {
        setMenuState((prev) => [...prev, ...newMenus]);
      } else {
        setFooterMenuState((prev) => [...prev, ...newMenus]);
      }
    }

    setBusinessMenu({
      business_tags: [],
    });
    setTagSearchInput("");
    setShowTagDropdown(false);
  };

  const updateItemInTree = (items, id, updateFn) =>
    items.map((item) =>
      item.id === id
        ? updateFn(item)
        : {
            ...item,
            children: updateItemInTree(item.children || [], id, updateFn),
          }
    );

  // Collapses repeated entries and folds a duplicate's children into the entry
  // that is kept, so nothing is silently dropped by the name-keyed storage format.
  const dedupeMenuTree = (items) => {
    const byIdentity = new Map();
    const ordered = [];

    (items || []).forEach((item) => {
      const identity = getMenuIdentity(item);
      const existing = byIdentity.get(identity);

      if (existing) {
        existing.children = dedupeMenuTree([
          ...(existing.children || []),
          ...(item.children || []),
        ]);
        return;
      }

      const next = { ...item, children: dedupeMenuTree(item.children || []) };
      byIdentity.set(identity, next);
      ordered.push(next);
    });

    return ordered;
  };

  const buildTreeForSave = (items) =>
    items.map((item) => ({
      id: item.id,
      name: item.name,
      href: item.href,
      target: item.target,
      defaultName: item.defaultName,
      type: item.type,
      visibility: item.isEnable,
      ...(item.type === "pages" && {
        metaTitle: item.metaTitle || "",
        metaDescription: item.metaDescription || "",
      }),
      subMenu: buildTreeForSave(item.children || []),
    }));

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (menuType === "Header Menu") {
        const dedupedMenu = dedupeMenuTree(menuState);
        setMenuState(dedupedMenu);

        const menu_customize = {};
        dedupedMenu.forEach((item, index) => {
          const keyName =
            item.type === "customLink"
              ? `${item.name}-customlink`
              : item.type === "tag"
              ? `${item.name}-tag`
              : item.type === "pages"
              ? `${item.name}-page`
              : item.name;
          menu_customize[keyName] = {
            id: item.id,
            visibility: item.isEnable,
            href: item.href,
            ...(item?.defaultName && { defaultName: item?.defaultName }),
            type: item.type,
            target: item.target,
            order: index,
            ...(item.type === "pages" && {
              metaTitle: item.metaTitle || "",
              metaDescription: item.metaDescription || "",
            }),
            subMenu: buildTreeForSave(item.children || []),
          };
        });
        await handelThemeData({ menu_customize });
      } else {
        const footer_customize = {};
        footerMenuState.forEach((item, index) => {
          const keyName =
            item.type === "customLink"
              ? `${item.name}-customlink`
              : item.type === "tag"
              ? `${item.name}-tag`
              : item.type === "pages"
              ? `${item.name}-page`
              : item.name;
          footer_customize[keyName] = {
            visibility: item.isEnable,
            href: item.href,
            type: item.type,
            target: item.target,
            order: index,
            ...(item.type === "pages" && {
              metaTitle: item.metaTitle || "",
              metaDescription: item.metaDescription || "",
            }),
            subMenu: [], // Footer doesn't support nested items
          };
        });
        await handelThemeData({ footer_customize });
      }
    } finally {
      setIsSaving(false);
    }
  };

  // ✅ Prepare available menus (left side)
  const availableStaticMenus = staticMenus.map((name) => ({
    id: name,
    name,
    type: "static",
    href: menuUrlMapping[name],
    defaultName: name,
    target: false,
    isEnable: true,
    children: [],
  }));

  const toggleSection = (key) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setfunneldata((prev) => ({ ...prev, [name]: value }));
  };

  const handleWordpressToggle = async (pendingChecked) => {
    const payload = {
      _id: selectedTheme?._id,
      agency_id: selectedTheme?.agency_id,
      enable_wordpress: pendingChecked,
    };
    const { response } = await Service.agency.enableWordpress({ payload });
    if (response) {
      Update(response?.data);
      loadedMenuSignature.current = null;
      loadedFooterSignature.current = null;
      setMenuState([]);
      setFooterMenuState([]);
    }
  };

  const handleOpenConfirm = (e) => {
    setPendingChecked(e.target.checked);
    setOpenConfirm(true);
  };

  const handleConfirm = () => {
    handleWordpressToggle(pendingChecked);
    setOpenConfirm(false);
  };

  const handleCancel = () => {
    setPendingChecked(null);
    setOpenConfirm(false);
  };

  const currentMenuState = menuType === "Header Menu" ? menuState : footerMenuState;
  const canHaveChildren = menuType === "Header Menu";
  const updateCurrentMenuItem = (id, fields) => {
    const update = (items) =>
      updateItemInTree(items, id, (item) => ({ ...item, ...fields }));
    if (menuType === "Header Menu") {
      setMenuState((items) => update(items));
    } else {
      setFooterMenuState((items) => update(items));
    }
  };

  const renderPageSeoEditors = (items, depth = 0) =>
    (items || []).flatMap((item) => {
      const editor =
        item.type === "pages" ? (
          <div
            key={`seo-${item.id}`}
            style={{
              marginLeft: `${depth * 20}px`,
              marginTop: "12px",
              padding: "12px",
              border: "1px solid #dcdcde",
              borderRadius: "4px",
              background: "#f9f9f9",
            }}
          >
            <strong style={{ display: "block", marginBottom: "8px" }}>
              SEO: {item.name}
            </strong>
            <input
              type="text"
              value={item.metaTitle || ""}
              onChange={(event) =>
                updateCurrentMenuItem(item.id, { metaTitle: event.target.value })
              }
              maxLength={60}
              placeholder="Optional meta title"
              className="custom-input"
              style={{ width: "100%", maxWidth: "100%" }}
            />
            <textarea
              value={item.metaDescription || ""}
              onChange={(event) =>
                updateCurrentMenuItem(item.id, {
                  metaDescription: event.target.value,
                })
              }
              maxLength={160}
              rows={3}
              placeholder="Optional meta description"
              className="custom-input"
              style={{ width: "100%", maxWidth: "100%", resize: "vertical" }}
            />
            <small>
              {(item.metaTitle || "").length}/60 title ·{" "}
              {(item.metaDescription || "").length}/160 description
            </small>
          </div>
        ) : null;
      return [
        editor,
        ...renderPageSeoEditors(item.children || [], depth + 1),
      ].filter(Boolean);
    });

  // console.log("enabledHomeToggle", enabledHomeToggle)

  return (
    <div className="wp-menu-builder sd-settings-branding-tab">
      <style jsx>{`
        .wp-menu-builder {
          background: #f1f1f1;
          min-height: 70vh;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu,
            Cantarell, "Helvetica Neue", sans-serif;
        }

        .wp-header {
          background: #fff;
          border-bottom: 1px solid #ccd0d4;
          padding: 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          box-shadow: 0 1px 1px rgba(0, 0, 0, 0.04);
        }

        .wp-title {
          font-size: 23px;
          font-weight: 400;
          margin: 0;
          color: #23282d;
        }

        .wp-button {
          background: #0073aa;
          border: 1px solid #0073aa;
          color: #fff;
          padding: 8px 12px;
          border-radius: 3px;
          cursor: pointer;
          font-size: 13px;
          text-decoration: none;
          display: inline-block;
          transition: all 0.1s ease-in-out;
        }

        .wp-button:hover {
          background: #005a87;
          border-color: #005a87;
        }

        .wp-button:disabled {
          background: #ccc;
          border-color: #ccc;
          cursor: not-allowed;
        }

        .wp-content {
          display: flex;
          max-width: 1200px;
          margin: 20px auto;
          gap: 20px;
          padding: 0 20px;
        }

        .wp-sidebar {
          width: 500px;
          background: #fff;
          border: 1px solid #ccd0d4;
          border-radius: 4px;
          height: fit-content;
        }

        .wp-sidebar-section {
          border-bottom: 1px solid #ccd0d4;
        }

        .wp-sidebar-section:last-child {
          border-bottom: none;
        }

        .wp-sidebar-header {
          background: #f9f9f9;
          padding: 12px 15px;
          border-bottom: 1px solid #ccd0d4;
          font-weight: 600;
          color: #23282d;
          font-size: 14px;
        }

        .wp-sidebar-content {
          padding: 15px;
        }

        .wp-menu-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;
          border-bottom: 1px solid #f0f0f1;
        }

        .wp-menu-item:last-child {
          border-bottom: none;
        }

        .wp-menu-name {
          font-size: 13px;
          color: #23282d;
        }

        .wp-add-button {
          background: #f7f7f7;
          border: 1px solid #ccd0d4;
          color: #0073aa;
          padding: 4px 8px;
          border-radius: 3px;
          cursor: pointer;
          font-size: 11px;
          text-decoration: none;
          transition: all 0.1s ease-in-out;
        }

        .wp-add-button:hover {
          background: #0073aa;
          color: #fff;
        }

        .wp-main-content {
          flex: 1;
          background: #fff;
          border: 1px solid #ccd0d4;
          border-radius: 4px;
        }

        .wp-main-header {
          background: #f9f9f9;
          padding: 12px 15px;
          border-bottom: 1px solid #ccd0d4;
          font-weight: 600;
          color: #23282d;
          font-size: 14px;
        }

        .wp-main-body {
          padding: 15px;
          min-height: 400px;
        }

        .wp-menu-structure-item {
          background: #fafafa;
          border: 1px solid #e1e1e1;
          border-radius: 4px;
          margin-bottom: 10px;
          padding: 12px 15px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          cursor: move;
          transition: all 0.1s ease-in-out;
        }

        .wp-menu-structure-item:hover {
          background: #f0f0f1;
          border-color: #c3c4c7;
        }

        .wp-menu-structure-item.dragging {
          opacity: 0.5;
          transform: rotate(5deg);
        }

        .wp-menu-structure-item.drag-over {
          border-color: #0073aa;
          background: #e5f5ff;
        }

        .wp-menu-item-content {
          display: flex;
          align-items: center;
          gap: 10px;
          flex: 1;
        }

        .wp-drag-handle {
          color: #8c8f94;
          cursor: move;
          font-size: 16px;
        }

        .wp-menu-item-name {
          font-size: 14px;
          color: #23282d;
          font-weight: 500;
        }

        .wp-menu-item-url {
          font-size: 12px;
          color: #646970;
          margin-left: 5px;
        }

        .wp-menu-controls {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .wp-toggle {
          position: relative;
          display: inline-block;
          width: 36px;
          height: 20px;
        }

        .wp-toggle input {
          opacity: 0;
          width: 0;
          height: 0;
        }

        .wp-toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #ccc;
          transition: 0.4s;
          border-radius: 20px;
        }

        .wp-toggle-slider:before {
          position: absolute;
          content: "";
          height: 16px;
          width: 16px;
          left: 2px;
          bottom: 2px;
          background-color: white;
          transition: 0.4s;
          border-radius: 50%;
        }

        .wp-toggle input:checked + .wp-toggle-slider {
          background-color: #0073aa;
        }

        .wp-toggle input:checked + .wp-toggle-slider:before {
          transform: translateX(16px);
        }

        .wp-remove-button {
          background: none;
          border: none;
          color: #d63638;
          cursor: pointer;
          font-size: 16px;
          padding: 4px;
          border-radius: 3px;
          transition: all 0.1s ease-in-out;
        }

        .wp-remove-button:hover {
          background: #d63638;
          color: #fff;
        }

        .wp-empty-state {
          text-align: center;
          padding: 40px 20px;
          color: #646970;
          font-style: italic;
        }

        .wp-section-title {
          font-size: 12px;
          font-weight: 600;
          color: #646970;
          text-transform: uppercase;
          margin: 15px 0 10px 0;
          letter-spacing: 0.5px;
        }

        .wp-section-title:first-child {
          margin-top: 0;
        }
        .custom-input {
          width: 225px;
          max-width: 225px;
          padding: 12px 15px;
          margin: 8px 0;
          border: 2px solid #ddd;
          border-radius: 10px;
          font-size: 16px;
          outline: none;
          transition: all 0.3s ease;
          background: #f9f9f9;
          box-shadow: inset 0 2px 5px rgba(0, 0, 0, 0.05);
        }

        .custom-input:focus {
          border-color: #4a90e2;
          background: #fff;
          box-shadow: 0 0 8px rgba(74, 144, 226, 0.4);
        }

        .custom-input::placeholder {
          color: #999;
          font-style: italic;
        }
        .menu-select-container {
          width: 16rem;
          font-family: "Inter", sans-serif;
        }

        .menu-select-wrapper {
          position: relative;
        }

        .menu-select {
          width: 100%;
          appearance: none;
          background-color: white;
          border: 1px solid #d1d5db;
          border-radius: 0.75rem;
          padding: 0.75rem 2.5rem 0.75rem 1rem;
          color: #374151;
          font-size: 1rem;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
          transition: all 0.2s ease;
        }

        .menu-select:focus {
          outline: none;
          border-color: #6366f1;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2);
        }

        .menu-select-arrow {
          position: absolute;
          right: 0.75rem;
          top: 50%;
          width: 1.25rem;
          height: 1.25rem;
          color: #9ca3af;
          transform: translateY(-50%);
          pointer-events: none;
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>

      <div className="wp-header">
        <div
          style={{ display: "flex", gap: "30px", justifyContent: "center", alignItems: "center" }}
        >
          <h1 className="wp-title">Menus</h1>
          <div className="menu-select-container">
            <div className="menu-select-wrapper">
              <select
                id="menuType"
                className="menu-select"
                value={menuType}
                onChange={handleMenuTypeChange}
              >
                <option>Header Menu</option>
                <option>Footer Menu</option>
              </select>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="menu-select-arrow"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </div>
        </div>

        <PrimaryButton type="button" onClick={handleSave} disabled={isSaving}>
          {isSaving ? "Saving Menu..." : "Save Menu"}
        </PrimaryButton>
      </div>

      <div>
        <div>
          <h3 style={{ margin: "30px" }}>{menuType}</h3>
        </div>
        <div className="wp-content">
          {/* Left sidebar */}
          <div className="wp-sidebar">
            <div className="wp-sidebar-section">
              <div className="wp-sidebar-header">Add menu items</div>
              <div className="wp-sidebar-content">
                {/* Static Pages */}
                <div className="wp-section">
                  <div
                    className="wp-section-title"
                    onClick={() => toggleSection("static")}
                    style={{
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "space-between",
                      padding: "8px 12px",
                      background: "#f6f7f7",
                      border: "1px solid #dcdcde",
                      borderRadius: "4px",
                      marginBottom: "6px",
                    }}
                  >
                    <span>System Pages</span>
                    <span>{openSections.static ? "▲" : "▼"}</span>
                  </div>
                  {openSections.static && (
                    <div className="wp-section-content" style={{ marginLeft: "8px" }}>
                      {availableStaticMenus.map((m) => (
                        <div
                          key={m.id}
                          className="wp-menu-item"
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: "6px",
                          }}
                        >
                          <span className="wp-menu-name">{m.name}</span>
                          <button className="wp-add-button" onClick={() => handleAddMenu(m)}>
                            Add to Menu
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Pages */}
                <div className="wp-section">
                  <div
                    className="wp-section-title"
                    onClick={() => toggleSection("pages")}
                    style={{
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "space-between",
                      padding: "8px 12px",
                      background: "#f6f7f7",
                      border: "1px solid #dcdcde",
                      borderRadius: "4px",
                      marginBottom: "6px",
                    }}
                  >
                    <span>Funnel Pages</span>
                    <span>{openSections.pages ? "▲" : "▼"}</span>
                  </div>
                  {openSections.pages && (
                    <div className="wp-section-content" style={{ marginLeft: "8px" }}>
                      <div
                        className="wp-menu-item"
                        style={{
                          marginBottom: "6px",
                        }}
                      >
                        <button
                          style={{
                            padding: "16px 12px",
                            cursor: "pointer",
                            background: "transparent",
                            border: "1px solid #cccccc",
                            borderRadius: "5px",
                            color: "#808080",
                            fontWeight: "medium",
                            width: "100%",
                          }}
                          onClick={() => setOpenWhitelabelModal(true)}
                        >
                          Select Funnel
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="wp-section">
                  <div
                    className="wp-section-title"
                    onClick={() => toggleSection("tags")}
                    style={{
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "space-between",
                      padding: "8px 12px",
                      background: "#f6f7f7",
                      border: "1px solid #dcdcde",
                      borderRadius: "4px",
                      marginBottom: "6px",
                    }}
                  >
                    <span>Listing labels</span>
                    <span>{openSections.tags ? "▲" : "▼"}</span>
                  </div>
                  {openSections.tags && (
                    <div className="wp-section-content" style={{ marginLeft: "8px" }}>
                      <div style={{ marginBottom: "12px" }}>
                        {/* Selected Tags Display */}
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "6px",
                            marginBottom: "10px",
                          }}
                        >
                          {businessMenu.business_tags.map((tag) => (
                            <div
                              key={tag.value}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                backgroundColor: "#e8f4f8",
                                border: "1px solid #0073aa",
                                borderRadius: "4px",
                                padding: "4px 8px",
                                fontSize: "12px",
                                color: "#0073aa",
                              }}
                            >
                              {tag.label}
                              <button
                                onClick={() => handleRemoveTag(tag.value)}
                                style={{
                                  background: "none",
                                  border: "none",
                                  marginLeft: "6px",
                                  cursor: "pointer",
                                  color: "#0073aa",
                                  fontSize: "14px",
                                  padding: "0",
                                }}
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>

                        {/* Search Input */}
                        <div style={{ position: "relative", marginBottom: "10px" }}>
                          <input
                            type="text"
                            value={tagSearchInput}
                            onChange={(e) => handleTagSearch(e.target.value)}
                            onFocus={() => {
                              setFilteredTags(businessTags);
                              setShowTagDropdown(true);
                            }}
                            placeholder="Search or type tag name..."
                            style={{
                              width: "100%",
                              padding: "10px 12px",
                              border: "1px solid #ddd",
                              borderRadius: "4px",
                              fontSize: "13px",
                              boxSizing: "border-box",
                              outline: "none",
                              transition: "border-color 0.2s",
                            }}
                            onBlur={() => {
                              setTimeout(() => setShowTagDropdown(false), 200);
                            }}
                          />

                          {/* Dropdown */}
                          {showTagDropdown && (
                            <div
                              style={{
                                position: "absolute",
                                top: "100%",
                                left: 0,
                                right: 0,
                                backgroundColor: "#fff",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                marginTop: "4px",
                                maxHeight: "200px",
                                overflowY: "auto",
                                zIndex: 10,
                                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                              }}
                            >
                              {/* Existing Tags */}
                              {availableTagOptions.length > 0 ? (
                                availableTagOptions.map((tag) => (
                                  <div
                                    key={tag.value}
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      handleSelectTag(tag);
                                    }}
                                    style={{
                                      padding: "10px 12px",
                                      cursor: "pointer",
                                      borderBottom: "1px solid #f0f0f0",
                                      fontSize: "13px",
                                      transition: "background-color 0.2s",
                                    }}
                                    onMouseEnter={(e) =>
                                      (e.target.style.backgroundColor = "#f5f5f5")
                                    }
                                    onMouseLeave={(e) =>
                                      (e.target.style.backgroundColor = "transparent")
                                    }
                                  >
                                    {tag.label}
                                  </div>
                                ))
                              ) : (
                                <div
                                  style={{ padding: "10px 12px", color: "#999", fontSize: "13px" }}
                                >
                                  No tags found
                                </div>
                              )}

                              {/* Create New Tag Option */}
                              {tagSearchInput.trim() &&
                                !filteredTags.some(
                                  (t) => t.label.toLowerCase() === tagSearchInput.toLowerCase()
                                ) && (
                                  <div style={{ borderTop: "1px solid #ddd", padding: "8px 12px" }}>
                                    <button
                                      type="button"
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        if (!isCreatingTag) handleCreateNewTag();
                                      }}
                                      disabled={isCreatingTag}
                                      style={{
                                        width: "100%",
                                        padding: "6px 8px",
                                        backgroundColor: isCreatingTag ? "#ccc" : "#0073aa",
                                        color: "#fff",
                                        border: "none",
                                        borderRadius: "3px",
                                        cursor: isCreatingTag ? "not-allowed" : "pointer",
                                        fontSize: "12px",
                                        fontWeight: "500",
                                        transition: "background-color 0.2s",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: "6px",
                                      }}
                                      onMouseEnter={(e) => {
                                        if (!isCreatingTag)
                                          e.target.style.backgroundColor = "#005a87";
                                      }}
                                      onMouseLeave={(e) => {
                                        if (!isCreatingTag)
                                          e.target.style.backgroundColor = "#0073aa";
                                      }}
                                    >
                                      {isCreatingTag && (
                                        <span
                                          style={{
                                            display: "inline-block",
                                            width: "12px",
                                            height: "12px",
                                            border: "2px solid #fff",
                                            borderTop: "2px solid transparent",
                                            borderRadius: "50%",
                                            animation: "spin 0.6s linear infinite",
                                          }}
                                        />
                                      )}
                                      {isCreatingTag
                                        ? "Creating..."
                                        : `Create tag: ${tagSearchInput}`}
                                    </button>
                                  </div>
                                )}
                            </div>
                          )}
                        </div>
                      </div>

                      <div style={{ display: "flex", gap: "8px", marginBottom: "10px" }}>
                        <button
                          type="button"
                          className="wp-add-button"
                          onClick={handleAddBusinessTags}
                          disabled={businesstagLoader}
                        >
                          {businesstagLoader ? "Loading..." : "Add to Menu"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="wp-section">
                  <div
                    className="wp-section-title"
                    onClick={() => toggleSection("customLinks")}
                    style={{
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "space-between",
                      padding: "8px 12px",
                      background: "#f6f7f7",
                      border: "1px solid #dcdcde",
                      borderRadius: "4px",
                      marginBottom: "6px",
                    }}
                  >
                    <span>Custom Links</span>
                    <span>{openSections.customLinks ? "▲" : "▼"}</span>
                  </div>
                  {openSections.customLinks && (
                    <div className="wp-section-content" style={{ marginLeft: "8px" }}>
                      <div
                        style={{
                          marginBottom: "6px",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <input
                            type="text"
                            name="name"
                            value={CustomValue?.name}
                            placeholder="Name"
                            class="custom-input"
                            onChange={handleChange}
                          />
                          <input
                            type="text"
                            name="url"
                            value={CustomValue?.url}
                            placeholder="Url"
                            class="custom-input"
                            onChange={handleChange}
                          />
                        </div>
                        <p
                          style={{
                            color: "#721c24",
                            backgroundColor: "#f8d7da",
                            border: "1px solid #f5c6cb",
                            padding: "10px 15px",
                            borderRadius: "6px",
                            fontSize: "14px",
                            margin: "10px 0",
                            display: errorLink ? "block" : "none",
                          }}
                        >
                          ⚠️ {errorLink}
                        </p>
                        <button
                          className="wp-add-button"
                          style={{ float: "right", marginBottom: "20px", marginTop: "10px" }}
                          onClick={() => handleAddCustomMenuLink()}
                        >
                          Add to Menu
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Main content */}
          <div className="wp-main-content">
            <div className="wp-main-header">Menu structure</div>
            <div className="wp-main-body">
              {currentMenuState.length === 0 ? (
                <Typography sx={{ mt: 2, fontStyle: "italic" }}>No menu items added yet</Typography>
              ) : (
                <MenuBuilder
                  items={currentMenuState}
                  setItems={menuType === "Header Menu" ? setMenuState : setFooterMenuState}
                  canHaveChildren={() => canHaveChildren}
                  maxDepth={canHaveChildren ? 99 : 1}
                />
              )}
              {renderPageSeoEditors(currentMenuState)}
            </div>
          </div>
        </div>
      </div>

      {/* ... (keep the rest of your modal and dialog code) ... */}
      {openWhitelabelModal && (
        <div className="modal-zz-confirm">
          <div
            className="scrollable-modal-content bg-white rounded"
            style={{ border: "1px solid black", position: "relative" }}
          >
            <h2 className="font-14">Select Funnel</h2>
            <span className="crossison" onClick={() => setOpenWhitelabelModal(false)}>
              X
            </span>

            <div className="list-model" style={{ marginTop: "20px" }}>
              <div>
                <FormField
                  label="Name"
                  placeholder="Name"
                  name="name"
                  inputProps={{ type: "text" }}
                  value={funnelData.name}
                  onChange={handleInputChange}
                />
                <br />
                <label style={{ marginBottom: "10px", display: "block" }}>Select Funnel</label>
                <select
                  value={selectedFunnel?._id || ""}
                  onChange={handleFunnelChange}
                  style={{
                    width: "100%",
                    padding: "15px 10px",
                    marginBottom: "30px",
                    borderRadius: "10px",
                    border: "1px solid #D2D6DA",
                  }}
                >
                  <option value="" disabled>
                    Select Funnel
                  </option>
                  {funnels.map((funnel) => (
                    <option key={funnel._id} value={funnel._id}>
                      {funnel.name}
                    </option>
                  ))}
                </select>
                <br />

                {selectedFunnel && (
                  <>
                    <label style={{ marginBottom: "10px", display: "block" }}>Select Step</label>
                    <select
                      value={selectedStep?.id || ""}
                      onChange={handleStepChange}
                      style={{
                        width: "100%",
                        padding: "15px 10px",
                        marginBottom: "30px",
                        borderRadius: "10px",
                        border: "1px solid #D2D6DA",
                      }}
                    >
                      <option value="" disabled>
                        Select Step
                      </option>
                      {selectedFunnel.steps.map((step) => (
                        <option key={step.id} value={step.id}>
                          {step.name}
                        </option>
                      ))}
                    </select>
                    <br />

                    {selectedStep && (
                      <>
                        <label style={{ marginBottom: "10px", display: "block" }}>
                          Select Page
                        </label>
                        <select
                          value={funnelData.url || ""}
                          onChange={(e) => {
                            const selectedPageId = e.target.value;
                            setfunneldata((prev) => ({
                              ...prev,
                              url: `${env.GHL.PREVIEW_BASE_URL || "https://app.gohighlevel.com/v2/preview"}/${selectedPageId}`,
                            }));
                          }}
                          name="url"
                          style={{
                            width: "100%",
                            padding: "15px 10px",
                            marginBottom: "30px",
                            borderRadius: "10px",
                            border: "1px solid #D2D6DA",
                          }}
                        >
                          <option value="" disabled>
                            Select Page
                          </option>
                          {pagesForSelectedStep.map((page, idx) => (
                            <option key={idx} value={page}>
                              {page}
                            </option>
                          ))}
                        </select>
                      </>
                    )}
                  </>
                )}

                <br />
                <div
                  className="list-model1"
                  style={{ display: "flex", justifyContent: "flex-end" }}
                >
                  <button className="wp-add-button" onClick={() => handleAddFunnel()}>
                    Add to Menu
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <Dialog open={openConfirm} onClose={handleCancel}>
        <DialogTitle>WordPress Setting</DialogTitle>
        <DialogContent>
          <span style={{ color: "red" }}>WARNING:</span> The new all-in-one menu builder uses
          Listing labels only, and does not support Categories or Sub-Categories. Enabling the new
          all-in-one menu builder will erase your existing Categories and Sub-Categories. This
          cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="inherit">
            Go Back - Do Not Enable
          </Button>
          <Button onClick={handleConfirm} color="primary" variant="contained">
            Proceed
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Menu;
